import { inject, Injectable, NgZone } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { UserRole } from '@libs/shared/api';
import { KeycloakUserRole } from '@libs/shared/interface/enum';
import { AuthUserProfile } from '@sprint-expert/app/core/auth/auth-user-profile';
import { KeycloakOAuthService } from '@sprint-expert/app/core/auth/keycloak-oauth.service';
import { injectCoreFeature } from '@sprint-expert/app/core/state/core.service';
import { environment } from '@sprint-expert/environments/environment';
import { OAuthErrorEvent } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly core = injectCoreFeature();
  readonly oauthService = inject(KeycloakOAuthService);
  readonly zone = inject(NgZone);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly router = inject(Router);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  isAuthenticated$$ = toSignal(this.isAuthenticated$);

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  isDoneLoading$$ = toSignal(this.isDoneLoading$);

  canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map(values => values.every(b => b)));

  private navigateToLoginPage() {
    this.router.navigateByUrl('/home').then();
  }

  constructor() {
    this.core.expertCreated$.subscribe(() => {
      this.refreshToken().then(() =>
        this.router.navigateByUrl(`/expert-workspace-selection`).then()
      );
    });
    // Useful for debugging:
    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
        // Handle specific errors
        const errorReason = event.reason as any; // Cast to 'any' to access 'error' property
        if (errorReason && errorReason.error === 'interaction_required') {
          // Prompt user to re-authenticate
          this.login();
        }
        if (event.type === 'user_profile_load_error') {
          // Attempt to silently refresh the token first. If that fails, fallback to a hard logout.
          this.refreshToken()
            .catch(() => {
              // Refresh failed – clear local auth state and navigate to login page
              this.isAuthenticatedSubject$.next(false);
              this.navigateToLoginPage();
            });
        }
      } else {
        console.debug('OAuthEvent Object:', event);
      }
    });

    // Configure auth based on platform
    if (Capacitor.getPlatform() === 'ios') {
      this.configureIOS();
    } else if (Capacitor.getPlatform() === 'android') {
      this.configureAndroid();
    } else {
      this.configureWeb();
    }

    window.addEventListener('storage', event => {
      if (event.key !== 'auth_data_updated' && event.key !== null) {
        return;
      }
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );

      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });

    this.oauthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.events
      .pipe(filter(event => event.type === 'token_received'))
      .subscribe(() => this.oauthService.loadUserProfile());

    this.oauthService.events
      .pipe(
        filter(event =>
          ['session_terminated', 'session_error'].includes(event.type)
        )
      )
      .subscribe(event => {
        console.warn('Session issue:', event);
        // Optionally, attempt to refresh the token or prompt the user
      });

    this.oauthService.setupAutomaticSilentRefresh({}, 'access_token');

    // Handle silent refresh errors
    this.oauthService.events
      .pipe(filter(event => event.type === 'silent_refresh_error'))
      .subscribe((event: OAuthErrorEvent) => {
        console.error('Silent refresh error:', event);
        // Decide whether to retry or take other actions
        this.navigateToLoginPage();
      });

    // Handle explicit token refresh errors (e.g. when refreshToken() fails)
    this.oauthService.events
      .pipe(filter(event => event.type === 'token_refresh_error'))
      .subscribe((event: OAuthErrorEvent) => {
        console.error('Token refresh error event:', event);
        this.navigateToLoginPage();
      });
  }

  async runInitialLoginSequence(): Promise<void> {
    if (location.hash) {
      console.log('Encountered hash fragment, plotting as table...');
      console.table(
        location.hash
          .substr(1)
          .split('&')
          .map(kvp => kvp.split('='))
      );
    }

    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      if (this.oauthService.hasValidAccessToken()) {
        this.setAuthUser();

        return;
      }

      if (this.oauthService.getRefreshToken()) {
        await this.refreshToken();
        return;
      }

      // Handle the case where the user is not authenticated
    } catch (e) {
      console.error('Initialization error:', e);
      // Optionally handle the error, redirect, or notify the user
    } finally {
      this.isDoneLoadingSubject$.next(true);
    }
  }

  login(targetUrl?: string): void {
    this.oauthService.initLoginFlow(targetUrl || this.router.url, {
      prompt: 'login',
    });
  }

  logout(): void {
    this.oauthService.logOut();
  }

  get accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  async refreshToken(): Promise<void> {
    try {
      await this.oauthService.refreshToken();
      this.setAuthUser();
    } catch (error) {
      console.error('Token refresh error:', error);
      // Navigate user to login page if refresh fails.
      this.navigateToLoginPage();
      throw error; // Still propagate error for any further handling
    }
  }

  setAuthUser() {
    this.oauthService
      .loadUserProfile()
      .then((authUserProfile: AuthUserProfile) =>
        this.core.setAuthUser(authUserProfile)
      );
  }

  hasValidAccessToken(): boolean {
    const valid = this.oauthService.hasValidAccessToken();
    return valid && this.isDoneLoadingSubject$.value;
  }

  getUserProfile(): Observable<AuthUserProfile> {
    return from(
      this.oauthService.loadUserProfile() as Promise<AuthUserProfile>
    );
  }

  getCurrentUserRole(): UserRole {
    const userRoles = this.getRealmRoles();

    const roleMapping: { [key in KeycloakUserRole]: UserRole } = {
      [KeycloakUserRole.Admin]: UserRole.Admin,
      [KeycloakUserRole.SeniorManager]: UserRole.SeniorManager,
      [KeycloakUserRole.JuniorManager]: UserRole.JuniorManager,
      [KeycloakUserRole.PrepManager]: UserRole.PrepManager,
      [KeycloakUserRole.Expert]: UserRole.Expert,
      [KeycloakUserRole.PrepExpert]: UserRole.PrepExpert,
      [KeycloakUserRole.ExpertManager]: UserRole.ExpertManager,
      [KeycloakUserRole.PrepExpertManager]: UserRole.PrepExpertManager,
      [KeycloakUserRole.SilverMember]: UserRole.SilverMember,
      [KeycloakUserRole.BronzeMember]: UserRole.BronzeMember,
    };

    for (const keycloakRole of Object.keys(roleMapping) as KeycloakUserRole[]) {
      if (userRoles.includes(keycloakRole)) {
        return roleMapping[keycloakRole];
      }
    }

    return UserRole.BronzeMember;
  }

  getRealmRoles(): KeycloakUserRole[] {
    const idClaims = this.oauthService.getIdentityClaims();

    if (!idClaims) {
      console.error(
        "Couldn't get identity claims, make sure the user is signed in."
      );
      this.navigateToLoginPage();
      return [];
    }
    if (!idClaims['realm_access'] || !idClaims['realm_access']['roles']) {
      console.error(
        "Keycloak didn't provide realm_roles in the token. Have you configured the predefined mapper realm roles correctly?"
      );
      this.navigateToLoginPage();
      return [];
    }

    const realmRoles = idClaims['realm_access']['roles'];
    return realmRoles ?? [];
  }

  private configureWeb(): void {
    console.log('Using web configuration');
    this.oauthService.configure(environment.authConfig.web);
  }

  private configureIOS(): void {
    console.log('Using iOS configuration');
    this.oauthService.configure(environment.authConfig.ios);

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const url = new URL(event.url);
      if (url.host !== 'login') {
        return;
      }

      this.zone.run(() => {
        const queryParams: Params = {};
        const searchParams = url.searchParams;
        for (const [key, value] of searchParams.entries()) {
          queryParams[key] = value;
        }

        this.router
          .navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
            queryParamsHandling: 'merge',
          })
          .then(() => {
            this.oauthService.tryLogin().then(() => {});
            Browser.close().then();
          })
          .catch(error => {
            console.error(error);
            Browser.close().then();
          });
      });
    });
  }

  private configureAndroid(): void {
    console.log('Using Android configuration');
    this.oauthService.configure(environment.authConfig.android);

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const url = new URL(event.url);
      if (url.host !== 'login') {
        return;
      }

      this.zone.run(() => {
        const queryParams: Params = {};
        const searchParams = url.searchParams;
        for (const [key, value] of searchParams.entries()) {
          queryParams[key] = value;
        }

        this.router
          .navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
            queryParamsHandling: 'merge',
          })
          .then(() => {
            this.oauthService.tryLogin().then(() => {});
            Browser.close().then();
          })
          .catch(error => {
            console.error(error);
            Browser.close().then();
          });
      });
    });
  }
}
